import { ModalHandler } from "@lib/ModalHandler";
import {
  IDistCheckRespData,
  IProductAdditionRespData,
  TDistCheckItemData,
  TDistributionCheck,
  TDistroSectionType,
  TParsedItem,
  TPrimaryAnswerFlowName,
  TSectionData,
  TSectionStatus,
  TSurveyInfo,
  TTextSearchOptions,
  TVerboseFillResultsMap,
} from "@lib/types";
import {
  ALWAYS_IN_STOCK_NAME_RES,
  getNormalizedText,
  getSplitCleanly,
  randChoice,
  randNum,
  rollOnPercent,
} from "@lib/util/common";
import { Accessor } from "solid-js";
import { DIST_CHECK_PRIMARY_ANSWER_FLOWS } from "./presets";

export const VISIBLE_QUESTION_CONTAINER_SELECTOR = "#dataEntry > ul.list-group > li.q:not(.disabledQ)";

export type TDistroSectionCheckItems = [TSectionData, DistributionCheck[]];

type TSectionAwaiter = (
  modalHandler: Accessor<ModalHandler>,
  sectionName: RegExp | string,
  throwIfMissing: boolean,
  timeoutMinutes?: number
) => Promise<TDistroSectionType>;

export class SurveyInfo implements TSurveyInfo {
  readonly ch_added_stock = 60;
  readonly ch_oos = 10;

  clientName: string | null;
  max_added_stock: number;

  total_items: number;
  total_scanned_products: number;
  total_new_products: number;
  total_new_and_scanned_products: number;

  verboseFillResults: TVerboseFillResultsMap;
  distCheckRespData: IDistCheckRespData;
  distChecks: DistributionCheck[];

  distcheckByRawUpc: Record<string, DistributionCheck>;

  constructor(distChecks: DistributionCheck[], distCheckRespData: IDistCheckRespData) {
    this.max_added_stock = Math.floor(distChecks.length * (25 / 100));
    if (distChecks.length < 6 && this.max_added_stock <= 1) {
      this.max_added_stock = randChoice([1, 2]);
    }

    this.clientName = distCheckRespData.client_name;
    this.total_items = 0;

    this.total_scanned_products = 0;
    this.total_new_products = 0;
    this.total_new_and_scanned_products = 0;

    this.distChecks = distChecks;
    this.distCheckRespData = distCheckRespData;

    this.distcheckByRawUpc = {};

    this.verboseFillResults = {
      "Added stock": 0,
      "Confirmed in stock": 0,
      "In-Dist Ordered": 0,
      "Out-Of-Dist Ordered": 0,
      "Out-Of-Dist Suggested Order": 0,
    } satisfies TVerboseFillResultsMap;

    for (const productAddition of this.distCheckRespData.product_additions) {
      const distCheck = this.distChecks.find((distcheck) =>
        productAddition.product.raw_upcs.includes(distcheck.rawUpc)
      )!;

      distCheck.productAddition = productAddition;
    }

    this.decideDistResults();
  }

  getDistCheckbyRawUpc(rawUpc: string): DistributionCheck | undefined {
    return this.distcheckByRawUpc[rawUpc];
  }

  decideDistResults() {
    for (const distCheck of this.distChecks) {
      if (distCheck.rawUpc in this.distcheckByRawUpc) {
        const existingDistCheck = this.distcheckByRawUpc[distCheck.rawUpc];
        distCheck.flowResultName = existingDistCheck.flowResultName;
        continue;
      }

      this._decideDistResult(distCheck);
    }

    this._countItems();

    for (const distCheckQuestion of this.distChecks) {
      const flowResultName = distCheckQuestion.flowResultName;
      if (flowResultName === null) continue;

      const verboseFillResultNames = DIST_CHECK_PRIMARY_ANSWER_FLOWS.answer_flows[flowResultName].aliases;

      for (const verboseName of verboseFillResultNames) {
        this.verboseFillResults[verboseName] += 1;
      }
    }
  }

  private _decideDistResult(distCheck: DistributionCheck) {
    // FIXME: remove later
    if (this.clientName === "Post Foods, LLC") {
      distCheck.flowResultName = "no_tag_oos_suggested_order";
      this.distcheckByRawUpc[distCheck.rawUpc] = distCheck;
      return;
    }

    if (distCheck.productAddition === null) {
      console.warn(
        `distCheck.productAddition is null for product ${distCheck.rawUpc} -> '${distCheck.productName}'`
      );
      return;
    }

    distCheck.flowResultName = "no_tag_oos";
    this.distcheckByRawUpc[distCheck.rawUpc] = distCheck;

    if (
      distCheck.productAddition.is_carried &&
      rollOnPercent(this.ch_oos) &&
      !isAlwaysInStock(distCheck.productName)
    ) {
      distCheck.flowResultName = "tag_oos";
      this.distcheckByRawUpc[distCheck.rawUpc] = distCheck;
    } else if (distCheck.productAddition.is_carried) {
      distCheck.flowResultName = "tag_full_stock";
      this.distcheckByRawUpc[distCheck.rawUpc] = distCheck;

      if (
        this.verboseFillResults["Added stock"] < this.max_added_stock &&
        rollOnPercent(this.ch_added_stock)
      ) {
        distCheck.flowResultName = "tag_low_stock";
        this.distcheckByRawUpc[distCheck.rawUpc] = distCheck;
      }
    }
  }

  private _countItems() {
    for (const { productAddition } of this.distChecks) {
      this.total_items += 1;

      if (productAddition?.is_new === true) this.total_new_products += 1;
      if (productAddition?.is_carried === true) this.total_scanned_products += 1;
      if (productAddition?.is_new === true && productAddition.is_carried) {
        this.total_new_and_scanned_products += 1;
      }
    }
  }
}

export class DistributionCheck implements TDistributionCheck {
  rawUpc: string;
  productName: string;

  private _flowResultName: TPrimaryAnswerFlowName | null;
  unitsPacked: number | null = null;
  casesOrdered: number | null = null;

  productAddition: IProductAdditionRespData | null;

  constructor(parsedDistroItem: TParsedItem) {
    this.rawUpc = parsedDistroItem.rawUpc;
    this.productName = parsedDistroItem.name;

    this._flowResultName = null;

    this.productAddition = null;
  }

  set flowResultName(flowResultName: TPrimaryAnswerFlowName | null) {
    if (flowResultName === null) {
      this._flowResultName = null;
      return;
    }

    if (!(flowResultName in DIST_CHECK_PRIMARY_ANSWER_FLOWS.answer_flows)) {
      throw new Error(`Invalid flow result name: ${flowResultName}`);
    }

    this._flowResultName = flowResultName;
    if (this._flowResultName === "tag_low_stock") {
      this.unitsPacked = randNum(1, 3);
      this.casesOrdered = 0;
    } else if (this._flowResultName === "tag_oos") {
      this.unitsPacked = 0;
      this.casesOrdered = 1;
    }
  }

  get flowResultName() {
    return this._flowResultName;
  }
}

export function getDistCheckItem(listItemElm: HTMLElement): DistributionCheck | null {
  try {
    const parsedItem = getParsedItem(listItemElm);
    if (parsedItem === null) {
      throw new Error(`Parsed Item is null for ${listItemElm.innerText}`);
    }

    const distCheck = new DistributionCheck(parsedItem);
    return distCheck;
  } catch (error) {
    console.warn(error);
    return null;
  }
}

export function getDistCheckItems(): [DistributionCheck[], TDistCheckItemData[]] {
  const alertListElements = document.querySelectorAll<HTMLElement>("[id='IAAlertList'] > a.list-btn");
  const distChecks: DistributionCheck[] = [];
  const distCheckItemDatalist: TDistCheckItemData[] = [];

  for (const listItem of alertListElements) {
    const distCheck = getDistCheckItem(listItem);
    if (distCheck === null) {
      console.error("Item could not be parsed for list item:", listItem);
      continue;
    }

    distCheckItemDatalist.push({
      element: listItem,
      rawUpc: distCheck.rawUpc,
      productName: distCheck.productName,
    });

    distChecks.push(distCheck);
  }

  return [distChecks, distCheckItemDatalist];
}

export function getParsedItem(listItemElm: HTMLElement): TParsedItem | null {
  const upcRegex = /^ *upc *(\d(?:\s?\d){3,19}) *$/i;

  const lines = getSplitCleanly(listItemElm.innerText);

  const name = lines.at(0)?.trim();
  if (name === undefined) {
    console.warn("No name found in list item:", listItemElm.innerText);
    return null;
  }

  for (const line of lines) {
    const upcMatch = upcRegex.exec(line);
    if (upcMatch !== null) {
      return {
        name: name,
        rawUpc: upcMatch[1].replaceAll(/\s+/g, ""),
      } satisfies TParsedItem;
    }
  }

  return null;
}

export function isAlwaysInStock(productName: string) {
  return ALWAYS_IN_STOCK_NAME_RES.some((regexp) => regexp.exec(productName));
}

/**
 * @returns {boolean} indicating whether the section element was clicked or not
 */
export async function clickSurveySection(
  sectionName: string | RegExp,
  modalHandler: Accessor<ModalHandler>,
  sectionAwaiter: TSectionAwaiter,
  throwIfMissing: boolean,
  clickIfGreen = false,
  options: TTextSearchOptions = {}
): Promise<[boolean, TSectionStatus, TDistroSectionType]> {
  const { caseSensitive = false } = options;

  const openingSectionElm = getSurveySection(sectionName, { caseSensitive });

  if (openingSectionElm === null) {
    modalHandler().addErrorMsg("Cannot find survey section: " + sectionName.toString());
    return [false, "missing", null];
  }

  if (openingSectionElm.classList.contains("success")) {
    if (clickIfGreen) {
      console.warn("Section", sectionName, "is currently green, clicking anyway");
      openingSectionElm.click();
      const sectionType = await sectionAwaiter(modalHandler, sectionName, throwIfMissing);

      return [true, "complete", sectionType];
    } else {
      console.warn("Section", sectionName, "is currently green, skipping");
      return [false, "complete", null];
    }
  }

  openingSectionElm.click();
  const sectionType = await sectionAwaiter(modalHandler, sectionName, throwIfMissing);
  return [true, "incomplete", sectionType];
}

function getSurveySection(
  sectionName: string | RegExp,
  options: Pick<TTextSearchOptions, "caseSensitive"> = {}
): HTMLElement | null {
  const sections = getSurveySections(sectionName, options);
  return sections.at(0)?.sectionElement ?? null;
}

export function getSurveySections(
  sectionName: string | RegExp,
  options: Pick<TTextSearchOptions, "caseSensitive"> = {}
): TSectionData[] {
  const { caseSensitive = false } = options;
  const actionElements = document.querySelectorAll("[id^='actionLink']");
  const result: HTMLElement[] = [];

  for (const actionElement of actionElements) {
    const elm = actionElement as HTMLElement;

    // Remove trailing " (123)"
    let normalizedText = elm.innerText.replace(/\s*\(\d+\)\s*$/, "");
    normalizedText = getNormalizedText(normalizedText);

    if (!caseSensitive) {
      normalizedText = normalizedText.toLowerCase();
    }

    if (sectionName instanceof RegExp) {
      if (sectionName.test(normalizedText)) {
        result.push(elm);
      }
    } else {
      const target = caseSensitive ? sectionName : sectionName.toLowerCase();
      if (caseSensitive) normalizedText = normalizedText.toLowerCase();

      if (normalizedText.endsWith(target)) {
        result.push(elm);
      }
    }
  }

  return result.map((sectionElement) => ({
    title: sectionElement.innerText.trim(),
    sectionElement: sectionElement,
    isComplete: sectionElement.classList.contains("success"),
    distCheckItemDatalist: [],
  })) satisfies TSectionData[];
}

export function dispatchChangeEvents(element: HTMLElement) {
  element.dispatchEvent(new Event("input", { bubbles: true }));
  // element.dispatchEvent(new Event("change", { bubbles: true }));
  // element.dispatchEvent(new Event("blur", { bubbles: true }));
}

export function getSOID() {
  const targetSelector = ".alignWithAddr";
  const soidParentElm = document.querySelector<HTMLElement>(targetSelector);
  if (soidParentElm === null) {
    throw new Error(`${targetSelector} not found (needed for SOID)`);
  }

  const soid = extractSOID(soidParentElm.innerText);
  if (soid === null) {
    throw new Error(`Could not extract SOID from '${soidParentElm.innerText}'`);
  }

  return soid;
}

function extractSOID(input: string): number | null {
  const match = input.match(/\b\d+\b/);
  return match ? Number.parseInt(match[0], 10) : null;
}

export function isLocationMainServiceOrderMenu(): boolean {
  const targetUrlPattern = /^https:\/\/www\.qtraxweb\.com\/execute\/#\/TS\/SO\/\d+(\/Edit)?$/;
  return targetUrlPattern.test(window.location.href);
}

export function getQuestionContainer(
  currentElement: HTMLElement,
  { visible = true }: { visible?: boolean } = {}
): HTMLElement | null {
  const disabledQuery = visible ? ":not(.disabledQ)" : "";
  return currentElement.closest(`#dataEntry > ul.list-group > li.q${disabledQuery}`);
}

export function getNextVisibleQuestionContainer(currentElement: HTMLElement): HTMLElement | null {
  const selector = VISIBLE_QUESTION_CONTAINER_SELECTOR;
  let currentQuestionContainer = currentElement.closest(selector);

  while (currentQuestionContainer !== null) {
    const nextSibling = currentQuestionContainer.nextElementSibling;
    if (nextSibling instanceof HTMLElement && nextSibling.matches(selector)) {
      return nextSibling;
    }
    currentQuestionContainer = currentQuestionContainer.nextElementSibling as HTMLElement | null;
  }

  return null;
}

/** find the next grandchild element of questionContainer whose id has the form of "q<number>" */
export function getQTargetFromQuestionContainer(questionContainer: HTMLElement) {
  const targetSelector = "[id^='q']";
  const candidateElements = questionContainer.querySelectorAll<HTMLElement>(targetSelector);
  if (candidateElements.length === 0) {
    console.warn("No question target found in container", questionContainer);
    return null;
  }

  const idRegex = /^q\d+$/;
  const candidateElementsFiltered = [...candidateElements].filter((elm) => idRegex.test(elm.id));

  if (candidateElementsFiltered.length === 0) {
    console.warn("No valid question target found in container", questionContainer);
    return null;
  } else if (candidateElementsFiltered.length > 1) {
    throw new Error(
      `Multiple question targets found in container: ${candidateElementsFiltered.map((elm) => elm.id).join(", ")}`
    );
  }

  return candidateElementsFiltered[0];
}

export function getSaveBtnSafely() {
  const saveBtn = document.querySelector<HTMLButtonElement>("#save");
  if (saveBtn === null) {
    console.warn("Save button not found");
    throw new Error("Save button not found");
  }
  return saveBtn;
}

export function getJobName(): string {
  const elementId = "jobName";
  const jobNameElement = document.getElementById(elementId);
  if (jobNameElement === null) {
    console.warn(`Element with ID '${elementId}' not found`);
    throw new Error(`Element with ID '${elementId}' not found`);
  }

  return jobNameElement.innerText.trim();
}
